import webApiAdmin from '@/lib/api-common/webApiAdmin';

/* ── webApi2 dashboard 관련 ── */

/** Response shape returned by POST /admin/campaign/users/get */
interface AdminUsersGetResponse {
  users?: unknown[];
  totalPages?: number;
  favLabels?: unknown[];
}

/**
 * In-flight request cache for the new admin API.
 * Both getUsers and getUsersTotalPage are called in parallel with identical
 * params (Promise.all in loadInfluencers). This map lets them share a single
 * network request keyed on the serialised params string.
 * Entries are deleted after 5 s so stale data cannot linger across pages.
 */
const _usersGetPending = new Map<string, Promise<AdminUsersGetResponse>>();

/**
 * Sort fields the new endpoint accepts (FindUsersSort). The dashboard emits
 * `${field}_${asc|desc}` strings whose fields already match these — except
 * `updatedAt`, which has no server-side equivalent and is dropped.
 */
const _VALID_SORT_FIELDS = new Set([
  'subscribers', 'country', 'name', 'averageViews', 'averageViewers',
  'lastMessageAt', 'lowestPrice', 'highestPrice', 'kpiPricePerView',
  'kpiPricePerClick', 'lastVideoActivity', 'engagementViewersToSubscribersPercent',
  'engagementLikesPercent', 'engagementCommentPercent',
]);

/** Tri-state ('yes'/'no'/'any') boolean filters — 'any' is dropped upstream. */
const _BOOL_FILTER_KEYS = [
  'isRegistered', 'hasRates', 'hasDemographic', 'hasStreams',
  'youtubeWatchLiveStreams', 'updateTiktokSocialStats', 'updateTiktokChannelStats',
  'updateDouyinSocialStats', 'updateDouyinChannelStats', 'updateFacebookChannelStats',
] as const;

/** Comma-separated inputs the new schema expects as string[]. */
const _CSV_ARRAY_KEYS = ['country', 'language', 'keywords', 'tags', 'categories'] as const;

const _DEFAULT_SORT = { field: 'subscribers', order: 'desc' as const };

/** Split a comma-separated input into trimmed, non-empty values. */
function _splitCsv(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
  if (typeof v !== 'string') return [];
  return v.split(',').map((s) => s.trim()).filter(Boolean);
}

/** Parse a "min~max" string into a NumberRange, omitting blank/invalid bounds. */
function _parseNumberRange(v: unknown): { from?: number; to?: number } | undefined {
  if (typeof v !== 'string' || !v.trim()) return undefined;
  const [rawFrom, rawTo] = v.split('~');
  const range: { from?: number; to?: number } = {};
  const from = Number(rawFrom);
  const to = Number(rawTo);
  if (rawFrom?.trim() && Number.isFinite(from)) range.from = from;
  if (rawTo?.trim() && Number.isFinite(to)) range.to = to;
  return range.from != null || range.to != null ? range : undefined;
}

/** Parse a "${field}_${asc|desc}" sort string into a FindUsersSort (or undefined). */
function _parseSortBy(v: unknown): { field: string; order: 'asc' | 'desc' } | undefined {
  if (typeof v !== 'string') return undefined;
  const m = v.match(/^(.*)_(asc|desc)$/);
  if (!m) return undefined;
  const [, field, order] = m;
  if (!_VALID_SORT_FIELDS.has(field)) return undefined;
  return { field, order: order as 'asc' | 'desc' };
}

/** 'yes'/true → true, 'no'/false → false, anything else → undefined. */
function _parseTriBool(v: unknown): boolean | undefined {
  if (v === 'yes' || v === true) return true;
  if (v === 'no' || v === false) return false;
  return undefined;
}

/**
 * Map the dashboard's flat filter params to the new strict /admin/campaign/users/get
 * body: `{ parts, filter: {...} }`. `sortBy` and `pagination` are required by the
 * schema, so both are always sent (sort defaults to subscribers desc). `campaignId`
 * is omitted for a system-wide search ("all"). Unknown keys are stripped server-side.
 *
 * `genuineCampaignStatus` (the "has campaign history" filter) is intentionally not
 * mapped — the new schema only accepts it under `advancedFilters`, which is an
 * exclusive mode that would disable every other filter.
 */
function _buildUsersGetBody(
  params: Record<string, unknown>,
  parts: ('users' | 'totalPages')[],
): Record<string, unknown> {
  const page = typeof params.page === 'number' ? params.page : 1;
  const perPage = typeof params.perPage === 'number' ? params.perPage : 50;

  const filter: Record<string, unknown> = {
    pagination: { page, perPage },
    sortBy: _parseSortBy(params.sortBy) ?? _DEFAULT_SORT,
  };

  // Omit campaignId for a system-wide search ('all' / absent).
  if (typeof params.campaignId === 'string' && params.campaignId && params.campaignId !== 'all') {
    filter.campaignId = params.campaignId;
  }

  // Plain string filters.
  if (params.channelName) filter.channelName = params.channelName;
  if (params.channelId) filter.channelId = params.channelId;
  if (params.email) filter.email = params.email;
  if (params.inclusionComment) filter.inclusionComment = params.inclusionComment;
  if (params.recentGenre) filter.recentGenre = params.recentGenre;
  if (params.candidateGroupName) filter.candidateGroupName = params.candidateGroupName;

  // Comma-separated strings → string[].
  for (const key of _CSV_ARRAY_KEYS) {
    const arr = _splitCsv(params[key]);
    if (arr.length) filter[key] = arr;
  }

  // "min~max" ranges → NumberRange.
  const subs = _parseNumberRange(params.subscriberCount);
  if (subs) filter.subscriberCount = subs;
  const offer = _parseNumberRange(params.offer);
  if (offer) filter.offer = offer;

  // recentGame title → { title }.
  if (typeof params.recentGame === 'string' && params.recentGame.trim()) {
    filter.recentGame = { title: params.recentGame.trim() };
  }

  // Tri-state ('yes'/'no') → boolean.
  for (const key of _BOOL_FILTER_KEYS) {
    const b = _parseTriBool(params[key]);
    if (b != null) filter[key] = b;
  }

  // channelTypes is already an array; status is an array of UserCampaignStatus.
  if (Array.isArray(params.channelTypes) && params.channelTypes.length > 0) {
    filter.channelTypes = params.channelTypes;
  }
  if (Array.isArray(params.status) && params.status.length > 0) {
    filter.status = params.status;
  } else if (typeof params.status === 'string' && params.status) {
    filter.status = [params.status];
  }

  return { parts, filter };
}

/** Returns (or creates) a shared in-flight promise for the given params. */
function _getUsersGetPromise(
  params: Record<string, unknown>,
): Promise<AdminUsersGetResponse> {
  const key = JSON.stringify(params);
  const existing = _usersGetPending.get(key);
  if (existing) return existing;

  const promise = webApiAdmin.post<AdminUsersGetResponse>(
    'admin/campaign/users/get',
    // Always request both slices so either function can resolve from one call
    _buildUsersGetBody(params, ['users', 'totalPages']),
  ).then((r) => r ?? {});

  _usersGetPending.set(key, promise);
  // Auto-evict after 5 s to prevent stale cache across filter changes
  void promise.finally(() => setTimeout(() => _usersGetPending.delete(key), 5_000));
  return promise;
}

/**
 * Search users for a campaign (paginated, with posts/links/work-orders).
 * - Legacy: POST /api2/users { parts: 'users' }
 * - New:    POST /admin/campaign/users/get
 */
export async function getUsers(params: Record<string, unknown>): Promise<unknown> {
  const resp = await _getUsersGetPromise(params);
  const users = resp.users ?? [];
  // Build analysis from user.posts injected by _insertUserCampaignPosts in the new API.
  // This mirrors the legacy { analysis: { [userId]: { posts: [] } } } shape that
  // perUserAnalysis in influencers/index.tsx expects.
  const analysis: Record<string, { posts: unknown[] }> = {};
  for (const u of users) {
    const userId = String((u as Record<string, unknown>)._id ?? '');
    const posts = (u as Record<string, unknown>).posts as unknown[] | undefined;
    if (userId && posts?.length) {
      analysis[userId] = { posts };
    }
  }
  return { data: users, analysis };
}

/**
 * Get the total page count for the users query.
 * - Legacy: POST /api2/users { parts: 'totalPages' }
 * - New:    POST /admin/campaign/users/get (shared with getUsers call above)
 */
export async function getUsersTotalPage(params: Record<string, unknown>): Promise<unknown> {
  // Re-uses the in-flight promise started by getUsers (called in the same
  // Promise.all), so this adds no extra network request.
  const resp = await _getUsersGetPromise(params);
  return { totalPages: resp.totalPages ?? 1 };
}

/**
 * Exact user count for an arbitrary filtered query.
 * - Legacy: reads `usersCount` from POST /api2/users { parts: 'totalPages' }
 * - New:    POST /admin/campaign/users/get with perPage=1 so totalPages === count
 *
 * Do NOT use the shared in-flight cache here — perPage=1 would collide
 * with the normal paginated calls that use perPage=50.
 */
export async function getUsersExactCount(params: Record<string, unknown>): Promise<number> {
  const body = _buildUsersGetBody(params, ['totalPages']);
  // perPage=1 → totalPages == exact user count (ceil(n/1) == n)
  (body.filter as { pagination: Record<string, number> }).pagination.perPage = 1;
  const resp = await webApiAdmin.post<AdminUsersGetResponse>(
    'admin/campaign/users/get',
    body,
  ).then((r) => r ?? {});
  return resp.totalPages ?? 0;
}

/**
 * List candidate group names + counts for a campaign.
 * - Legacy: POST /api2/influencer_candidate_groups
 *           returns `{ [groupName]: count }`
 * - New:    POST /admin/campaign/influencer-candidate-groups/get
 *           returns `Array<{ groupName, influencerCount }>` — flatten back
 *           to the legacy record form so existing callers keep working.
 */
export async function getInfluencerCandidateGroups(
  params: { campaignId: string },
): Promise<Record<string, number>> {
  const list = await webApiAdmin.post<Array<{ groupName: string; influencerCount: number }>>(
    'admin/campaign/influencer-candidate-groups/get',
    params,
  );
  const record: Record<string, number> = {};
  for (const item of list ?? []) {
    if (item?.groupName) record[item.groupName] = item.influencerCount ?? 0;
  }
  return record;
}

const _statusCache = new Map<string, { data: unknown; expiresAt: number }>();
const STATUS_CACHE_TTL = 60_000; // 60초

/**
 * Per-status counts of users on a campaign — 60초 in-memory 캐시.
 * - Legacy: POST /api2/influencer_statuses
 * - New:    POST /admin/campaign/influencer-statuses/get
 */
export async function getInfluencerStatuses(
  params: { campaignId: string },
  options: { forceRefresh?: boolean } = {},
): Promise<unknown> {
  const key = params.campaignId;
  if (!options.forceRefresh) {
    const cached = _statusCache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.data;
  }
  const data = await webApiAdmin.post('admin/campaign/influencer-statuses/get', params);
  _statusCache.set(key, { data, expiresAt: Date.now() + STATUS_CACHE_TTL });
  return data;
}

/** 캠페인 상태 캐시 무효화 (상태 변경 후 호출) */
export function invalidateInfluencerStatusCache(campaignId: string): void {
  _statusCache.delete(campaignId);
}

/**
 * Budget completion + estimations for a campaign (or all campaigns).
 * - Legacy: POST /api2/campaigns_budget_completion
 *           returns `{ completion: { booked, actual, viaPayments }, estimations: {...} }`
 * - New:    POST /admin/campaign/budget-completion/get  (strict body)
 *           POST /admin/campaign/budget-completion/all-campaigns/get
 *           returns `{ completion: [{ part, amount }], estimations: {...} }` —
 *           transform the array back to the legacy record shape so existing
 *           callers (BudgetCompletion = { booked, actual, viaPayments }) work.
 */
export async function getBudgetCompletion(params: Record<string, unknown>): Promise<unknown> {
  const hasCampaignId = typeof params.campaignId === 'string' && params.campaignId.length > 0;
  const path = hasCampaignId
    ? 'admin/campaign/budget-completion/get'
    : 'admin/campaign/budget-completion/all-campaigns/get';
  // Strict zod schema: pick allowed fields only.
  const body: Record<string, unknown> = {};
  if (hasCampaignId) body.campaignId = params.campaignId;
  if (typeof params.withCommission === 'boolean') body.withCommission = params.withCommission;
  if (typeof params.status === 'string') body.status = params.status;
  const resp = await webApiAdmin.post<{
    completion?: Array<{ part: string; amount: number }>;
    estimations?: Record<string, unknown>;
  }>(path, body);
  const completion: Record<string, number> = {};
  for (const row of resp?.completion ?? []) {
    if (row?.part) completion[row.part] = row.amount ?? 0;
  }
  return { completion, estimations: resp?.estimations };
}

/**
 * Change `UserCampaignStatus` for one or more influencers on a campaign.
 * - Legacy: POST /api2/change_influencer_status
 * - New:    POST /admin/campaign/influencer-status/change
 */
export async function changeInfluencerStatus(params: Record<string, unknown>): Promise<unknown> {
  return webApiAdmin.post('admin/campaign/influencer-status/change', params);
}

/**
 * Load a user's payments for a campaign (influencer profile).
 * - Legacy: POST /api2/payment_get
 * - New:    POST /admin/payments/get (→ { payments, usersById, totalPages };
 *   the caller already unwraps `.payments`).
 */
export async function getPaymentsByUser(params: { userId: string | number; campaignId?: string }): Promise<unknown> {
  return webApiAdmin.post('admin/payments/get', params);
}

/**
 * Per-campaign stats lookup.
 * - Legacy: POST /api3/campaigns/getCampaignsStats
 * - New:    POST /admin/campaign/campaigns-stats/get
 */
export async function getCampaignsStats(campaignIds: string[]): Promise<unknown> {
  return webApiAdmin.post('admin/campaign/campaigns-stats/get', { campaignIds });
}
